import {useCallback, useEffect, useRef, useState} from 'react';
import {
  ChartingModality,
  ChartingQueue,
  ChartingQueueItem,
  completeSession,
  dismissSession,
  fetchChartingPendingCount,
  fetchChartingQueue,
  uncompleteSession,
  undismissSession,
} from '../services/chartingService';
import {logger, LogFlow, describeError} from '../services/logger';
import {getMockChartingQueue} from '../mocks/mockEhrFixtures';

const log = logger.scope(LogFlow.Charting);

// ---------------------------------------------------------------------------
// Shared pending-count store
//
// The sidebar badge (always mounted) and the charting page (mounted on demand)
// both care about the "pending charts" count. Rather than have them fetch
// independently and disagree, we keep a tiny module-level store: whoever loads
// the queue publishes the fresh count, and every subscriber re-renders. The
// badge still polls on a slow interval as a fallback for changes made on other
// devices.
// ---------------------------------------------------------------------------

let cachedPendingCount = 0;
const countListeners = new Set<(n: number) => void>();

function publishPendingCount(n: number) {
  cachedPendingCount = n;
  countListeners.forEach(listener => listener(n));
}

// How often the always-mounted badge re-checks the server. Charts are cleared
// at human speed, so a slow poll is plenty; same-session changes update the
// badge instantly via publishPendingCount.
const BADGE_POLL_INTERVAL_MS = 3 * 60 * 1000;

// Providers alt-tab between us and the EHR constantly, and every window focus
// used to refire the badge check — which is a full queue fetch server-side
// (~0.5s; there's no count-only endpoint yet). Focus refreshes only exist to
// catch changes made on OTHER devices, so once a minute is plenty.
const FOCUS_REFETCH_MIN_INTERVAL_MS = 60 * 1000;

// Lightweight hook for the sidebar badge: returns the current pending count,
// kept fresh by a slow poll plus any in-session updates other hooks publish.
// `enabled` gates polling so non-EHR accounts (which never see charting) don't
// fetch the queue at all.
export function useChartingQueueCount(
  enabled = true,
  mockMode = false,
): number {
  const [count, setCount] = useState(cachedPendingCount);

  useEffect(() => {
    if (!enabled) return undefined;
    if (mockMode) {
      const count = getMockChartingQueue().pendingCount;
      publishPendingCount(count);
      setCount(count);
      return undefined;
    }
    let active = true;
    countListeners.add(setCount);

    let lastLoadStartedAt = 0;
    const load = async () => {
      lastLoadStartedAt = Date.now();
      try {
        const next = await fetchChartingPendingCount();
        if (active) publishPendingCount(next);
      } catch {
        // Badge is non-critical: a failed poll just leaves the last count.
      }
    };

    load();
    const interval = setInterval(load, BADGE_POLL_INTERVAL_MS);
    const onFocus = () => {
      if (Date.now() - lastLoadStartedAt < FOCUS_REFETCH_MIN_INTERVAL_MS) {
        return;
      }
      load();
    };
    window.addEventListener('focus', onFocus);

    return () => {
      active = false;
      countListeners.delete(setCount);
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [enabled, mockMode]);

  return count;
}

interface UseChartingQueueResult {
  today: ChartingQueueItem[];
  backlog: ChartingQueueItem[];
  pendingCount: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  dismiss: (item: ChartingQueueItem) => Promise<void>;
  undismiss: (item: ChartingQueueItem) => Promise<void>;
  complete: (item: ChartingQueueItem) => Promise<void>;
  uncomplete: (item: ChartingQueueItem) => Promise<void>;
  markPushed: (conversationId: string) => void;
  markComplete: (conversationId: string) => void;
  markGenerated: (conversationId: string) => void;
}

// A row is "done" (checked off, no longer owed to the EHR) once it's been
// pushed or manually marked complete. Done rows stay visible — checked off in
// place — for the rest of the local day so the provider sees progress; the
// server stops returning them on the first fetch of the NEXT day.
const isDoneStatus = (status: ChartingQueueItem['status']) =>
  status === 'pushed' || status === 'complete';

const emptyQueue: ChartingQueue = {
  date: '',
  today: [],
  backlog: [],
  pendingCount: 0,
};

// Recompute the pending count from the two groups so optimistic mutations keep
// the badge honest without waiting for a server round-trip. Matches the
// backend's definition (rows still owed a chart): both groups can carry done
// rows — kept visible until the day rolls over — so both are filtered.
function computePending(
  today: ChartingQueueItem[],
  backlog: ChartingQueueItem[],
) {
  const pending = (rows: ChartingQueueItem[]) =>
    rows.filter(i => !isDoneStatus(i.status)).length;
  return pending(today) + pending(backlog);
}

function queueCounts(queue: ChartingQueue): Record<string, unknown> {
  return {
    today_count: queue.today.length,
    backlog_count: queue.backlog.length,
    pending_count: queue.pendingCount,
  };
}

function itemContext(item: ChartingQueueItem): Record<string, unknown> {
  return {
    conversation_id: item.conversationId,
    patient_id: item.patientId,
    ehr_patient_id: item.ehrPatientId,
    modality: item.modality,
    workflow_type: item.workflowType,
    charting_status: item.status,
    has_note: Boolean(item.noteGeneratedAt),
    has_ehr_push: Boolean(item.ehrPushedAt),
  };
}

// Full queue hook for the charting page.
export function useChartingQueue(mockMode = false): UseChartingQueueResult {
  const [queue, setQueue] = useState<ChartingQueue>(emptyQueue);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  // `loading` is for the FIRST load only. Refreshes (returning from a note,
  // undo, patient reassignment) keep showing the current queue and swap the
  // data in place — flashing the whole page back to a spinner on every
  // round-trip made the view feel like it was starting over.
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (mockMode) {
      const next = getMockChartingQueue();
      hasLoadedRef.current = true;
      setQueue(next);
      publishPendingCount(next.pendingCount);
      setLoading(false);
      setError(null);
      return undefined;
    }
    const controller = new AbortController();
    abortRef.current = controller;
    let active = true;
    const wasInitialLoad = !hasLoadedRef.current;
    if (wasInitialLoad) setLoading(true);
    setError(null);
    const startedAt = Date.now();
    log.info('queue_load_started', {
      is_initial_load: wasInitialLoad,
      reload_token: reloadToken,
    });

    fetchChartingQueue({signal: controller.signal})
      .then(next => {
        if (!active) return;
        hasLoadedRef.current = true;
        setQueue(next);
        publishPendingCount(next.pendingCount);
        log.info('queue_load_succeeded', {
          is_initial_load: wasInitialLoad,
          reload_token: reloadToken,
          duration_ms: Date.now() - startedAt,
          ...queueCounts(next),
        });
      })
      .catch(err => {
        if (!active || controller.signal.aborted) return;
        setError(
          err?.message || 'Could not load your review queue. Please retry.',
        );
        log.error('queue_load_failed', {
          is_initial_load: wasInitialLoad,
          reload_token: reloadToken,
          duration_ms: Date.now() - startedAt,
          ...describeError(err),
        });
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [reloadToken, mockMode]);

  const refresh = useCallback(() => {
    if (mockMode) return;
    log.info('queue_refresh_requested');
    setReloadToken(t => t + 1);
  }, [mockMode]);

  // Apply an optimistic patch to local state and keep the shared badge count
  // in sync, then run the server mutation; revert on failure.
  const applyAndSync = useCallback(
    (updater: (q: ChartingQueue) => ChartingQueue) => {
      setQueue(prev => {
        const next = updater(prev);
        const pendingCount = computePending(next.today, next.backlog);
        // Defer the cross-component publish (the sidebar badge subscribes to
        // this) out of React's render/commit phase so we never setState another
        // component while this one is rendering.
        queueMicrotask(() => publishPendingCount(pendingCount));
        return {...next, pendingCount};
      });
    },
    [],
  );

  const removeItem = (
    q: ChartingQueue,
    conversationId: string,
  ): ChartingQueue => ({
    ...q,
    today: q.today.filter(i => i.conversationId !== conversationId),
    backlog: q.backlog.filter(i => i.conversationId !== conversationId),
  });

  const restoreItem = (q: ChartingQueue, item: ChartingQueueItem) => {
    const isToday =
      item.startTime?.slice(0, 10) === new Date().toISOString().slice(0, 10);
    return {
      ...q,
      today: isToday ? [item, ...q.today] : q.today,
      backlog: isToday ? q.backlog : [item, ...q.backlog],
    };
  };

  const dismiss = useCallback(
    async (item: ChartingQueueItem) => {
      log.info('queue_item_dismiss_requested', itemContext(item));
      applyAndSync(q => removeItem(q, item.conversationId));
      if (mockMode) return;
      try {
        await dismissSession(item.conversationId);
        log.info('queue_item_dismiss_succeeded', itemContext(item));
      } catch (err) {
        // Revert by reloading the authoritative queue.
        log.error('queue_item_dismiss_failed', {
          ...itemContext(item),
          ...describeError(err),
        });
        refresh();
        throw err;
      }
    },
    [applyAndSync, refresh, mockMode],
  );

  const undismiss = useCallback(
    async (item: ChartingQueueItem) => {
      log.info('queue_item_undismiss_requested', itemContext(item));
      if (mockMode) {
        applyAndSync(q => restoreItem(q, item));
        return;
      }
      try {
        await undismissSession(item.conversationId);
        log.info('queue_item_undismiss_succeeded', itemContext(item));
      } catch (err) {
        log.error('queue_item_undismiss_failed', {
          ...itemContext(item),
          ...describeError(err),
        });
        throw err;
      } finally {
        refresh();
      }
    },
    [refresh, mockMode, applyAndSync],
  );

  // Flip a row to "complete" locally (kept visible + checked off in place,
  // whether it's from today or the backlog), then persist. Reverts to the
  // authoritative queue on failure. The row drops off naturally on the first
  // fetch of the next day.
  const markCompleteLocal = (q: ChartingQueue, conversationId: string) => {
    const now = new Date().toISOString();
    const flip = (i: ChartingQueueItem): ChartingQueueItem =>
      i.conversationId === conversationId
        ? {...i, status: 'complete', completedAt: now}
        : i;
    return {
      ...q,
      today: q.today.map(flip),
      backlog: q.backlog.map(flip),
    };
  };

  const complete = useCallback(
    async (item: ChartingQueueItem) => {
      log.info('queue_item_complete_requested', itemContext(item));
      applyAndSync(q => markCompleteLocal(q, item.conversationId));
      if (mockMode) return;
      try {
        await completeSession(item.conversationId);
        log.info('queue_item_complete_succeeded', itemContext(item));
      } catch (err) {
        log.error('queue_item_complete_failed', {
          ...itemContext(item),
          ...describeError(err),
        });
        refresh();
        throw err;
      }
    },
    [applyAndSync, refresh, mockMode],
  );

  const uncomplete = useCallback(
    async (item: ChartingQueueItem) => {
      log.info('queue_item_uncomplete_requested', itemContext(item));
      if (mockMode) {
        applyAndSync(q => {
          const restore = (row: ChartingQueueItem) =>
            row.conversationId === item.conversationId ? item : row;
          return {
            ...q,
            today: q.today.map(restore),
            backlog: q.backlog.map(restore),
          };
        });
        return;
      }
      try {
        await uncompleteSession(item.conversationId);
        log.info('queue_item_uncomplete_succeeded', itemContext(item));
      } catch (err) {
        log.error('queue_item_uncomplete_failed', {
          ...itemContext(item),
          ...describeError(err),
        });
        throw err;
      } finally {
        // The pre-complete status (draft / needs_note) is server-derived, so
        // reload rather than guess it.
        refresh();
      }
    },
    [refresh, mockMode, applyAndSync],
  );

  // Optimistically flip a row to "complete" (used by the background push
  // manager once a push lands, alongside markPushed). Persistence of the push
  // itself happens elsewhere; this only keeps the list honest.
  const markComplete = useCallback(
    (conversationId: string) => {
      log.info('queue_item_marked_complete_locally', {
        conversation_id: conversationId,
      });
      applyAndSync(q => markCompleteLocal(q, conversationId));
    },
    [applyAndSync],
  );

  // Optimistically flip a row to "pushed" after a push succeeds. The row stays
  // visible, checked off, so the provider sees the queue fill in with green
  // rather than watching entries silently vanish.
  const markPushed = useCallback(
    (conversationId: string) => {
      log.info('queue_item_marked_pushed_locally', {
        conversation_id: conversationId,
      });
      applyAndSync(q => {
        const flip = (i: ChartingQueueItem): ChartingQueueItem =>
          i.conversationId === conversationId
            ? {...i, status: 'pushed', ehrPushedAt: new Date().toISOString()}
            : i;
        return {
          ...q,
          today: q.today.map(flip),
          backlog: q.backlog.map(flip),
        };
      });
    },
    [applyAndSync],
  );

  // Optimistically flip a row from "needs note" to "note ready" (draft) once a
  // note has been generated on the right — leaves already-pushed rows alone.
  const markGenerated = useCallback(
    (conversationId: string) => {
      log.info('queue_item_note_generated_locally', {
        conversation_id: conversationId,
      });
      applyAndSync(q => {
        const flip = (i: ChartingQueueItem): ChartingQueueItem =>
          i.conversationId === conversationId && i.status === 'needs_note'
            ? {...i, status: 'draft', noteGeneratedAt: new Date().toISOString()}
            : i;
        return {
          ...q,
          today: q.today.map(flip),
          backlog: q.backlog.map(flip),
        };
      });
    },
    [applyAndSync],
  );

  return {
    today: queue.today,
    backlog: queue.backlog,
    pendingCount: queue.pendingCount,
    loading,
    error,
    refresh,
    dismiss,
    undismiss,
    complete,
    uncomplete,
    markPushed,
    markComplete,
    markGenerated,
  };
}
